/**
 * Off-main-thread twin of {@link ./vmgReferenceDelta} for `@nodets` worker pools.
 *
 * `@nodets` ships a task to a worker by SOURCE (`fn.toString()`), so a pool task may reference nothing
 * from its module scope — no imports, no module constants, no sibling functions. And `deps:` entries
 * are eval'd independently of one another, so the mutually-recursive VMG helper cluster (which also
 * needs the `speedUnits` resolvers) CANNOT be split across deps. The only workable shape is therefore a
 * single, fully self-contained task function with every helper — including the two `speedUnits`
 * magnitude resolvers — nested inside it and all constants inlined.
 *
 * This is a deliberate, verbatim transcription of the logic in `vmgReferenceDelta.ts`. That module stays
 * the human-readable source of truth and the test oracle; `__tests__/unit/vmgReferenceDeltaTask.test.ts`
 * asserts this task produces byte-identical output on the same fixtures, so the two cannot silently drift.
 *
 * Orchestration ({@link computeReferenceVmgDeltaGroups}) is a two-phase fan-out: build the reference
 * boat's cumulative series once (`pool.run`), then enrich every group in parallel (`pool.map`, one task
 * per boat), keeping the reference series compact rather than shipping the raw reference rows N times.
 */
import type { Pool } from '@utils/parallel/poolManager';
import { warn as logWarn } from '../../../../utils/console';
import { REFERENCE_DELTA_WARMUP_MS, countUnmeasuredSamples, getMapPointTimeMs } from './vmgReferenceDelta';
import type { MapPointRow, SourceGroup, CumulativePoint } from './vmgReferenceDelta';

/** Discriminated task input: build the reference series, or enrich one boat's rows against it. */
export type VmgDeltaTaskPayload =
  | { kind: 'series'; points: MapPointRow[]; t0Ms: number; vmgField: string }
  | {
      kind: 'group';
      rows: MapPointRow[];
      t0Ms: number;
      vmgField: string;
      refSeries: CumulativePoint[];
      isRef: boolean;
    };

export type VmgDeltaTaskResult = CumulativePoint[] | MapPointRow[];

/**
 * The self-contained pool task. Handles both phases via `payload.kind`. MUST NOT reference anything
 * outside its own body (see file header) — everything is nested/inlined on purpose.
 */
export function vmgDeltaTask(payload: VmgDeltaTaskPayload): VmgDeltaTaskResult {
  // ---- inlined constants (mirror vmgReferenceDelta.ts) ----
  const KPH_TO_MPS = 1 / 3.6;
  const KPH_PER_KNOT = 1.852;
  const TIMELINE_VMG_GAP_THRESHOLD_MS = 3000;
  const BSP_LEG_NUMBERS = [1]; // legs measured on BSP instead of |VMG| (the reach off the start)
  const MAX_MADE_GOOD_SPEED_KPH = 150;
  const MAX_SPEED_HOLD_MS = 3000;

  // ---- inlined speedUnits helpers (closure-free) ----
  function convertSpeedMagnitude(value: number, from: string, to: string): number {
    if (!Number.isFinite(value) || from === to) {return value;}
    if (from === 'kts' && to === 'kph') {return value * KPH_PER_KNOT;}
    if (from === 'kph' && to === 'kts') {return value / KPH_PER_KNOT;}
    return value;
  }
  function physicalUnitForRowKey(key: string): string {
    const lower = key.toLowerCase();
    if (lower.endsWith('_kts')) {return 'kts';}
    if (lower.endsWith('_kph')) {return 'kph';}
    return 'kts';
  }
  function bspRowLookupKeys(primaryField: string): string[] {
    return [
      primaryField,
      primaryField.toLowerCase(),
      primaryField.toUpperCase(),
      'Bsp_kph',
      'Bsp_kts',
      'bsp_kph',
      'bsp_kts',
      'Bsp',
      'bsp',
    ];
  }
  function vmgRowLookupKeys(primaryField: string): string[] {
    return [
      primaryField,
      primaryField.toLowerCase(),
      primaryField.toUpperCase(),
      'Vmg_kph',
      'Vmg_kts',
      'vmg_kph',
      'vmg_kts',
      'Vmg',
      'vmg',
    ];
  }
  function magnitudeInDisplayUnit(
    row: Record<string, unknown>,
    keys: string[],
    displayUnit: string
  ): number {
    for (const k of keys) {
      const v = row[k];
      let n: number;
      if (typeof v === 'number' && Number.isFinite(v)) {n = v;}
      else if (typeof v === 'string' && v !== '' && !Number.isNaN(Number(v))) {n = Number(v);}
      else {continue;}
      return convertSpeedMagnitude(n, physicalUnitForRowKey(k), displayUnit);
    }
    return Number.NaN;
  }
  function bspMagnitudeInDisplayUnit(row: Record<string, unknown>, primaryField: string): number {
    return magnitudeInDisplayUnit(row, bspRowLookupKeys(primaryField), 'kph');
  }
  function vmgMagnitudeInDisplayUnit(row: Record<string, unknown>, primaryField: string): number {
    return magnitudeInDisplayUnit(row, vmgRowLookupKeys(primaryField), 'kph');
  }

  // ---- inlined vmgReferenceDelta helpers ----
  function getMapPointTimeMs(row: MapPointRow): number {
    const raw = row?.Datetime ?? row?.timestamp ?? row?.time ?? row?.datetime;
    if (raw == null) {return NaN;}
    if (raw instanceof Date) {return raw.getTime();}
    if (typeof raw === 'number') {
      if (!Number.isFinite(raw)) {return NaN;}
      return raw > 1e12 ? raw : raw * 1000;
    }
    const t = new Date(raw as string).getTime();
    return Number.isFinite(t) ? t : NaN;
  }
  function numFromRow(row: MapPointRow, keys: string[]): number {
    for (const k of keys) {
      const v = row?.[k];
      if (typeof v === 'number' && Number.isFinite(v)) {return v;}
      if (typeof v === 'string' && v !== '' && !Number.isNaN(Number(v))) {return Number(v);}
    }
    return NaN;
  }
  function wrap180(deg: number): number {
    let d = ((deg + 180) % 360) - 180;
    if (d <= -180) {d += 360;}
    return d;
  }
  function absTwaDegFromRow(row: MapPointRow): number {
    const direct = numFromRow(row, ['Twa_deg', 'twa_deg', 'Twa', 'twa', 'TWA']);
    if (Number.isFinite(direct)) {return Math.abs(direct);}
    const twd = numFromRow(row, ['Twd_deg', 'twd_deg', 'Twd', 'twd', 'TWD']);
    const course = numFromRow(row, [
      'Cog_deg', 'cog_deg', 'Cog', 'cog', 'COG',
      'Hdg_deg', 'hdg_deg', 'Hdg', 'hdg', 'HDG', 'Heading',
    ]);
    if (Number.isFinite(twd) && Number.isFinite(course)) {
      return Math.abs(wrap180(twd - course));
    }
    return NaN;
  }
  function legNumberFromRow(row: MapPointRow): number {
    const raw = row?.leg_number ?? row?.Leg_number ?? row?.Leg ?? row?.LEG;
    const n = Number(raw);
    return Number.isFinite(n) ? Math.trunc(n) : NaN;
  }
  function raceNumberFromRow(row: MapPointRow): number {
    const raw = row?.Race_number ?? row?.race_number ?? row?.Race ?? row?.RACE;
    const n = Number(raw);
    return Number.isFinite(n) ? Math.trunc(n) : NaN;
  }
  function raceAtTime(series: CumulativePoint[], tMs: number): number {
    if (!series || series.length === 0) {return NaN;}
    if (tMs < series[0].tMs || tMs > series[series.length - 1].tMs) {return NaN;}
    let lo = 0;
    let hi = series.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (series[mid].tMs <= tMs) {lo = mid;}
      else {hi = mid - 1;}
    }
    const race = series[lo].race;
    return typeof race === 'number' && Number.isFinite(race) ? race : NaN;
  }
  function bspKphFromRow(row: MapPointRow): number {
    const b = bspMagnitudeInDisplayUnit(row as Record<string, unknown>, 'Bsp');
    return Number.isFinite(b) ? Math.abs(b) : NaN;
  }
  function vmgKphMagnitudeFromRow(row: MapPointRow, vmgField: string): number {
    const v = vmgMagnitudeInDisplayUnit(row as Record<string, unknown>, vmgField);
    if (Number.isFinite(v)) {return Math.abs(v);}
    const bspKph = bspKphFromRow(row);
    const absTwa = absTwaDegFromRow(row);
    if (Number.isFinite(bspKph) && Number.isFinite(absTwa)) {
      return Math.abs(bspKph * Math.cos((absTwa * Math.PI) / 180));
    }
    return NaN;
  }
  function isBspLeg(row: MapPointRow): boolean {
    const leg = legNumberFromRow(row);
    return Number.isFinite(leg) && BSP_LEG_NUMBERS.includes(leg);
  }
  function madeGoodSpeedKphFromRow(row: MapPointRow, vmgField: string): number {
    if (isBspLeg(row)) {
      const bspKph = bspKphFromRow(row);
      if (Number.isFinite(bspKph)) {return bspKph;}
    }
    return vmgKphMagnitudeFromRow(row, vmgField);
  }
  function buildCumulativeVmgSeries(
    sortedPoints: MapPointRow[],
    t0Ms: number,
    vmgField: string
  ): CumulativePoint[] {
    const out: CumulativePoint[] = [];
    let cumM = 0;
    let prevT: number | null = null;
    let started = false;
    const gapSec = TIMELINE_VMG_GAP_THRESHOLD_MS / 1000;
    const maxStepM = (MAX_MADE_GOOD_SPEED_KPH / 3.6) * gapSec;
    let heldSpeedKph = NaN;
    let holdStartMs: number | null = null;
    for (const row of sortedPoints || []) {
      const tMs = getMapPointTimeMs(row);
      if (!Number.isFinite(tMs) || tMs < t0Ms) {continue;}
      const race = raceNumberFromRow(row);
      if (!started) {
        started = true;
        prevT = tMs;
        out.push({ tMs, cumM: 0, race });
        continue;
      }
      const dtSec = (tMs - (prevT as number)) / 1000;
      let unmeasured = false;
      if (dtSec > 0 && dtSec <= gapSec) {
        const rawKph = madeGoodSpeedKphFromRow(row, vmgField);
        const usable = Number.isFinite(rawKph) && rawKph >= 0 && rawKph <= MAX_MADE_GOOD_SPEED_KPH;
        let speedKph: number;
        if (usable) {
          speedKph = rawKph;
          heldSpeedKph = rawKph;
          holdStartMs = null;
        } else {
          if (holdStartMs == null) {holdStartMs = tMs;}
          speedKph = tMs - holdStartMs <= MAX_SPEED_HOLD_MS ? heldSpeedKph : NaN;
        }
        if (Number.isFinite(speedKph)) {
          cumM += Math.min(speedKph * KPH_TO_MPS * dtSec, maxStepM);
        } else {
          unmeasured = true;
        }
      }
      prevT = tMs;
      out.push(unmeasured ? { tMs, cumM, race, unmeasured: true } : { tMs, cumM, race });
    }
    return out;
  }
  function interpolateCumulativeMeters(series: CumulativePoint[], tMs: number): number {
    if (!series || series.length === 0) {return NaN;}
    if (tMs <= series[0].tMs) {return series[0].unmeasured ? NaN : series[0].cumM;}
    const last = series[series.length - 1];
    if (tMs >= last.tMs) {return last.unmeasured ? NaN : last.cumM;}
    let lo = 0;
    let hi = series.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (series[mid].tMs < tMs) {lo = mid + 1;}
      else {hi = mid;}
    }
    const b = series[lo];
    const a = series[lo - 1];
    if (a.unmeasured || b.unmeasured) {return NaN;}
    const span = b.tMs - a.tMs;
    if (span <= 0) {return b.cumM;}
    const frac = (tMs - a.tMs) / span;
    return a.cumM + (b.cumM - a.cumM) * frac;
  }

  // ---- dispatch ----
  if (payload.kind === 'series') {
    return buildCumulativeVmgSeries(payload.points, payload.t0Ms, payload.vmgField);
  }
  const { rows, t0Ms, vmgField, refSeries, isRef } = payload;
  if (isRef) {
    return rows.map((row) => ({ ...row, _vmgDeltaM: 0 }));
  }
  // `t0Ms` here is already the integration start (warmup applied by the orchestrator).
  const boatSeries = buildCumulativeVmgSeries(rows, t0Ms, vmgField);
  // Window over which this boat and the reference are BOTH measured, with each side's cumulative at
  // its start, so the delta is a difference of increments rather than of two unrelated origins.
  const hasOverlap = boatSeries.length > 0 && refSeries.length > 0;
  const sharedStartMs = hasOverlap ? Math.max(boatSeries[0].tMs, refSeries[0].tMs) : NaN;
  const sharedEndMs = hasOverlap
    ? Math.min(boatSeries[boatSeries.length - 1].tMs, refSeries[refSeries.length - 1].tMs)
    : NaN;
  const overlaps = hasOverlap && sharedStartMs <= sharedEndMs;
  const boatBaseM = overlaps ? interpolateCumulativeMeters(boatSeries, sharedStartMs) : NaN;
  const refBaseM = overlaps ? interpolateCumulativeMeters(refSeries, sharedStartMs) : NaN;

  const enriched = rows.map((row) => {
    const tMs = getMapPointTimeMs(row);
    if (!Number.isFinite(tMs)) {return { ...row, _vmgDeltaM: NaN };}
    if (tMs < t0Ms) {return { ...row, _vmgDeltaM: 0 };} // warmup: no delta yet
    if (!overlaps || tMs < sharedStartMs || tMs > sharedEndMs) {
      return { ...row, _vmgDeltaM: NaN }; // no reference to measure against here
    }
    const rowRace = raceNumberFromRow(row);
    const refRace = raceAtTime(refSeries, tMs);
    if (Number.isFinite(rowRace) && Number.isFinite(refRace) && rowRace !== refRace) {
      return { ...row, _vmgDeltaM: NaN }; // different races are not comparable
    }
    const boatCum = interpolateCumulativeMeters(boatSeries, tMs);
    const refCum = interpolateCumulativeMeters(refSeries, tMs);
    const usable =
      Number.isFinite(boatCum) && Number.isFinite(refCum) &&
      Number.isFinite(boatBaseM) && Number.isFinite(refBaseM);
    const delta = usable ? (boatCum - boatBaseM) - (refCum - refBaseM) : NaN;
    return { ...row, _vmgDeltaM: delta };
  });
  // Never comparable to this reference -> drop the warmup zeros too (no stub line at 0).
  const everComparable = enriched.some(
    (row) => Number.isFinite(row._vmgDeltaM) && getMapPointTimeMs(row) >= t0Ms
  );
  return everComparable ? enriched : enriched.map((row) => ({ ...row, _vmgDeltaM: NaN }));
}

/**
 * Two-phase parallel orchestration mirroring `applyReferenceVmgDeltaToGroups`: reference series once
 * (`pool.run`), then one enrich task per boat (`pool.map`). Returns NEW group objects with cloned rows;
 * inputs are not mutated. `warmupMs` (default 2 min) pins every boat to 0 over the head of the window
 * and starts the integration at `t0Ms + warmupMs`. No-ops (returns `groups` unchanged) when the
 * reference boat is absent, and flattens everyone to 0 when its cumulative series is empty — identical
 * to the synchronous version.
 */
export async function computeReferenceVmgDeltaGroups(
  poolInstance: Pool<VmgDeltaTaskPayload, VmgDeltaTaskResult>,
  groups: SourceGroup[],
  referenceSourceId: number,
  t0Ms: number,
  vmgField: string,
  warmupMs: number = REFERENCE_DELTA_WARMUP_MS
): Promise<SourceGroup[]> {
  if (!Array.isArray(groups) || groups.length === 0) {return groups;}
  const refGroup = groups.find((g) => Number(g.sourceId) === Number(referenceSourceId));
  if (!refGroup) {return groups;}

  // Everything below integrates from the post-warmup start; the tasks zero any row before it.
  const startMs = t0Ms + (Number.isFinite(warmupMs) ? Math.max(0, warmupMs) : 0);

  const refSeries = (await poolInstance.run({
    kind: 'series',
    points: refGroup.data,
    t0Ms: startMs,
    vmgField,
  })) as CumulativePoint[];
  if (refSeries.length === 0) {
    return groups.map((group) => ({
      ...group,
      data: group.data.map((row) => ({ ...row, _vmgDeltaM: 0 })),
    }));
  }

  const refUnmeasured = countUnmeasuredSamples(refSeries);
  if (refUnmeasured > 0) {
    // The reference boat is the datum for every other boat, so its bad stretches blank the whole
    // fleet's delta. Surface it — otherwise this reads as "the chart stopped working".
    logWarn('[vmgReferenceDelta] Reference boat has unusable speed samples; fleet delta is blank there', {
      referenceSourceId,
      unmeasuredSamples: refUnmeasured,
      refSamples: refSeries.length,
    });
  }

  const enrichedRows = (await poolInstance.map(
    groups.map((group) => ({
      kind: 'group' as const,
      rows: group.data,
      t0Ms: startMs,
      vmgField,
      refSeries,
      isRef: Number(group.sourceId) === Number(referenceSourceId),
    }))
  )) as MapPointRow[][];

  // Report boats whose trace is materially blanked by bad data rather than letting a short line
  // read as a complete one.
  const blanked = groups
    .map((group, i) => {
      const rows = enrichedRows[i] || [];
      const scored = rows.filter((r) => getMapPointTimeMs(r) >= startMs);
      const bad = scored.filter((r) => !Number.isFinite(r._vmgDeltaM as number)).length;
      return { sourceId: group.sourceId, bad, scored: scored.length };
    })
    .filter((s) => s.scored > 0 && s.bad / s.scored > 0.02);
  if (blanked.length > 0) {
    logWarn('[vmgReferenceDelta] Boats with unusable speed data; those stretches are not plotted', {
      referenceSourceId,
      boats: blanked.map((s) => `${s.sourceId}: ${s.bad}/${s.scored} samples`),
    });
  }

  return groups.map((group, i) => ({ ...group, data: enrichedRows[i] }));
}
